import { DefaultLanguageProvider } from "./DefaultLanguageProvider";
import { ILanguage, ILanguageProvider } from "./Interfaces";
import { Localisation } from "./Localisation";

export class LanguageOptions
{
    private localisation: Localisation;
    public languages: ILanguage[] = [];
    public defaultLanguage!: string;
    public languageProvider!: ILanguageProvider

    constructor(localisation: Localisation)
    {
        this.localisation = localisation;
    }

    public useLanguages(...languages: ILanguage[]): LanguageOptions
    {
        if (languages.length == 0 || languages[0].code != this.localisation.terms)
        {
            throw new Error('The first language must have an ID defined by localisation.terms');
        }
        this.languages = languages;
        return this;
    }

    public setDefaultLanguage(defaultLanguage: string): LanguageOptions
    {
        this.defaultLanguage = defaultLanguage;
        return this;
    }

    public useDefaultLanguageProvider(...languages: string[]): LanguageOptions
    {
        this.languageProvider = new DefaultLanguageProvider();
        return this;
    }

    public async build(...languages: string[]): Promise<LanguageOptions>
    {
        const currentLanguage = await this.languageProvider.getCurrent();
        const browserLanguages = await this.languageProvider.getBrowserLanguages();
        let languageCodes = [currentLanguage, ...browserLanguages].filter(i => i != '');

        // fallback to base languages
        for (let i = 0; i < languageCodes.length; i++)
        {
            const languageOnly = languageCodes[i].split('-')[0] ?? '';
            const hasLanguage = languageCodes.some(i => i == languageOnly);

            if (hasLanguage)
            {
                continue;
            }

            const indexOflastRegion = languageCodes.findLastIndex(i => i.startsWith(languageOnly));
            languageCodes.splice(indexOflastRegion + 1, 0, languageOnly);
        }

        // get first valid language
        const allLanguageCodes = this.languages.map(i => i.code);
        const current = languageCodes.find(i => allLanguageCodes.includes(i)) ?? this.defaultLanguage;
        await this.setCurrent(current);
        return this;
    }

    private _current = '';
    public get current(): string
    {
        return this._current;
    }

    public async setCurrent(value: string): Promise<void>
    {
        this._current = value;
        await this.languageProvider.setCurrent(value);
    }
}
